import type { WorkflowConfig, FieldDefinition } from '@gzl10/nexus-sdk'

/**
 * Validate workflow config at startup. Throws descriptive error on invalid config.
 */
export function validateWorkflowConfig(workflow: WorkflowConfig, entityName: string): void {
  const prefix = `Workflow config for '${entityName}':`

  if (workflow.states.length < 2) {
    throw new Error(`${prefix} must have at least 2 states`)
  }

  const stateValues = new Set<string>()
  for (const state of workflow.states) {
    if (stateValues.has(state.value)) {
      throw new Error(`${prefix} duplicate state value '${state.value}'`)
    }
    stateValues.add(state.value)
  }

  if (!stateValues.has(workflow.initial)) {
    throw new Error(`${prefix} initial '${workflow.initial}' not found in states`)
  }

  for (const t of workflow.transitions) {
    if (!stateValues.has(t.from)) {
      throw new Error(`${prefix} transition from '${t.from}' references unknown state`)
    }
    if (!stateValues.has(t.to)) {
      throw new Error(`${prefix} transition to '${t.to}' references unknown state`)
    }
  }
}

/**
 * Inject a select field into entity fields from workflow.states.
 * Skips injection if a field with the same name already exists (user override).
 */
export function injectWorkflowField(
  fields: Record<string, unknown>,
  workflow: WorkflowConfig
): void {
  const fieldName = workflow.field ?? 'status'

  if (fields[fieldName]) return

  const field: FieldDefinition = {
    label: workflow.fieldLabel ?? { en: 'Status', es: 'Estado' },
    input: 'select',
    defaultValue: workflow.initial,
    options: {
      static: workflow.states.map(s => ({
        value: s.value,
        label: s.label,
        icon: s.icon,
      })),
    },
    db: { type: 'string', size: 50, nullable: false },
    meta: { sortable: true, searchable: false, showInDisplay: true, showInForm: true },
  }

  fields[fieldName] = field
}
